import React, { useState, useEffect } from 'react';
import { Lightbulb, TrendingUp, TrendingDown, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';

interface Insight {
  id: string;
  type: 'opportunity' | 'warning' | 'trend' | 'recommendation';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  actionUrl?: string;
  actionLabel?: string;
  metrics?: {
    label: string;
    value: string;
    trend?: 'up' | 'down';
  }[];
}

interface AIInsightsPanelProps {
  insights?: Insight[];
  loading?: boolean;
  onRefresh?: () => void;
}

const emptyInsights: Insight[] = [];

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  insights = emptyInsights,
  loading = false,
  onRefresh
}) => {
  const { t } = useTranslation();
  const [visibleInsights, setVisibleInsights] = useState<Insight[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setVisibleInsights(showAll ? insights : insights.slice(0, 3));
  }, [insights, showAll]);

  const getTypeIcon = (type: Insight['type']) => {
    switch (type) {
      case 'opportunity':
        return <Lightbulb className="h-5 w-5 text-yellow-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'trend':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'recommendation':
        return <Lightbulb className="h-5 w-5 text-blue-500" />;
      default:
        return <Lightbulb className="h-5 w-5 text-gray-500" />;
    }
  };

  const getTypeBg = (type: Insight['type']) => {
    switch (type) {
      case 'opportunity':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'warning':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      case 'trend':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'recommendation':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      default:
        return 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600';
    }
  };

  const getImpactBadge = (impact: Insight['impact']) => {
    const colors = {
      high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };

    const labels = {
      high: t('common.highImpact', 'Alto impacto'),
      medium: t('common.mediumImpact', 'Médio impacto'),
      low: t('common.lowImpact', 'Baixo impacto')
    };

    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[impact]}`}>
        {labels[impact]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
            <Lightbulb className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('dashboard.aiInsights', 'Insights de IA')}
          </h2>
        </div>
        {onRefresh && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            className="text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>

      {insights.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{t('dashboard.noInsights', 'Nenhum insight disponível no momento')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleInsights.map((insight) => (
            <div
              key={insight.id}
              className={`p-4 rounded-lg border ${getTypeBg(insight.type)} transition-all hover:shadow-sm`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getTypeIcon(insight.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {insight.title}
                    </h3>
                    {getImpactBadge(insight.impact)}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {insight.description}
                  </p>

                  {insight.metrics && insight.metrics.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                      {insight.metrics.map((metric, idx) => (
                        <div key={idx} className="flex items-center space-x-1 min-w-0">
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {metric.label}:
                          </span>
                          <span className={`text-sm font-medium whitespace-nowrap ${
                            metric.trend === 'up' ? 'text-green-600' :
                            metric.trend === 'down' ? 'text-red-600' :
                            'text-gray-900 dark:text-white'
                          }`}>
                            {metric.value}
                          </span>
                          {metric.trend && (
                            metric.trend === 'up'
                              ? <TrendingUp className="h-3 w-3 text-green-500 flex-shrink-0" />
                              : <TrendingDown className="h-3 w-3 text-red-500 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {insight.actionUrl && (
                    <a
                      href={insight.actionUrl}
                      className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {insight.actionLabel || t('common.viewMore', 'Ver mais')}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}

          {insights.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              {showAll
                ? t('common.showLess', 'Mostrar menos')
                : t('common.showMore', `Mostrar mais (${insights.length - 3})`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AIInsightsPanel;
